import React, { useEffect, useState } from "react";
import store from "../../redux/store";
import {
  Card,
  Flex,
  Heading,
  Input,
  InputGroup,
  InputRightElement,
  Spacer,
  Td,
  Tr,
} from "@chakra-ui/react";
import { FiEdit, FiEye, FiSearch, FiTrash } from "react-icons/fi";
import TableView from "../../components/TableView";
import { useDispatch, useSelector } from "react-redux";
import CustomButton from "../../components/ButtonComp";
import CustomToolTip from "../../components/CustomTooltip";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import ConfirmDialog from "../../components/ConfirmDialog";
import { toast } from "react-toastify";
import { get_bank_list, update_bank_status, check_cash_account } from "../../redux/bankSlice";
import { baseUrl } from "../../utils/utils";
import GasCylinderLoader from "../../components/GasCylinderLoader";

const BankList = () => {
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [initialValue, setInitialValue] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const [rows, setRows] = useState(10);
  const [page, setPage] = useState(1);
  const [deleteId, setDeleteId] = useState(null);
  const dispatch = useDispatch();
  const location = useLocation();

  const [searchParams] = useSearchParams();
  const fromParam = searchParams.get("from");
  const fromCash = fromParam === "cash";

  const { bank_list, total_bank_count, hasCashInSystem,loading } = useSelector(
    (state) => state.banks
  );

  // 🔍 Local state for filtering
  const [searchQuery, setSearchQuery] = useState("");

  // Filter logic
  const filteredBanks = bank_list?.filter((bank) => {
    const query = searchQuery.toLowerCase();
    return (
      bank.bankname?.toLowerCase().includes(query) ||
      bank.bank_branch?.toLowerCase().includes(query) ||
      bank.ac_type?.toLowerCase().includes(query) ||
      bank.accountno?.toLowerCase().includes(query) ||
      bank.ifsc?.toLowerCase().includes(query)
    );
  });

  useEffect(() => {
    // Check if cash account exists in the system when component mounts
    dispatch(check_cash_account());
  }, [dispatch]);

  const handleDelete = async () => {
    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append("action", "delete");
      formData.append("id", deleteId);

      const response = await fetch(baseUrl + `bank.php`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Failed to delete bank: ${response.statusText}`);
      }

      const result = await response.json();

      if (result?.success) {
        dispatch(get_bank_list({ page, rows }));
        dispatch(check_cash_account()); // Re-check cash account after deletion
        toast.success(result?.message || "Bank deleted successfully");
        setDeleteId(null);
        setDeleteDialog(false);
      } else {
        toast.error(result?.message || "Failed to delete bank");
      }
    } catch (error) {
      console.error("Error deleting bank:", error);
      toast.error("Failed to delete bank");
    } finally {
      setIsLoading(false);
    }
  };

  const viewBankDetails = (bankId) => {
    navigate(`bank-detail/${bankId}`, {
      state: { page, rows },
    });
  };

  useEffect(() => {
    if (location.state) {
      const { page: savedPage, rows: savedRows } = location.state;
      if (savedPage) setPage(savedPage);
      if (savedRows) setRows(savedRows);
    }
  }, [location.state]);

  useEffect(() => {
    dispatch(get_bank_list({ page, rows }));
    setInitialValue((page - 1) * rows + 1);
  }, [page, rows, dispatch]);

  const handleRowsChange = (e) => {
    const newRows = Number(e.target.value);
    setRows(newRows);
    setPage(1);
  };

  const handlePrevious = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };

  const handleNext = () => {
    if (page * rows < total_bank_count) {
      setPage(page + 1);
    }
  };

  return (
    <>
      <Card
        bg="#fff"
        paddingInline={5}
        paddingBlock={2}
        flexDirection={["column", "row"]}
        alignItems={["stretch", "center"]}
        gap={3}
        flexWrap="wrap"
      >
        <Heading fontSize="xl">Banks</Heading>
        <Flex
          direction={["column", "row"]}
          gap={2}
          alignItems="center"
          justifyContent={["flex-start", "flex-end"]}
          flex="1"
          flexWrap="wrap"
        >
          <InputGroup bg="#f4f5f7" borderRadius={5} w={["100%", "sm"]}>
            <Input
              placeholder="Search by Bank Name, Branch, Account Type, Account No., IFSC..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <InputRightElement>
              <FiSearch />
            </InputRightElement>
          </InputGroup>

          {/* Only show Add Cash button if no cash account exists in the entire system */}
          {!hasCashInSystem && (
            <CustomButton
              title="Add Cash"
              onClick={() =>
                navigate("/banks/add-cash", { state: { page, rows } })
              }
              w={["100%", "auto"]}
            />
          )}
          <CustomButton
            title="Add Bank"
            onClick={() =>
              navigate("/banks/add-bank", { state: { page, rows } })
            }
            w={["100%", "auto"]}
          />
        </Flex>
      </Card>

      <div>
      {loading ? (<GasCylinderLoader size="sm" loadingText="Loading Banks"/>):(
  <>
  <TableView
          headData={[
            "",
            "Bank Name",
            "Branch Name",
            "Account Type",
            "Account No.",
            "IFSC Code",
            "Opening Balance",
            "Closing Balance",
            "Action",
          ]}
          body={filteredBanks?.map((bank, index) => (
            <Tr key={bank.id}>
              <Td textAlign="center">
                {!(fromCash && bank.ac_type?.toLowerCase() === "cash") && (
                  <input
                    type="radio"
                    name="defaultBank"
                    checked={bank.de_status === "1"}
                    onChange={() => {
                      dispatch(update_bank_status(bank.id)).then((res) => {
                        if (res.meta.requestStatus === "fulfilled") {
                          toast.success(
                            res.payload?.message || "Default bank updated"
                          );
                          dispatch(get_bank_list({ page, rows }));
                        } else {
                          toast.error(
                            res.payload || "Failed to update default status"
                          );
                        }
                      });
                    }}
                  />
                )}
              </Td>

              <Td textAlign="center">{bank.bankname}</Td>
              <Td textAlign="center">{bank.bank_branch}</Td>
              <Td textAlign="center">{bank.ac_type}</Td>
              <Td textAlign="center">{bank.accountno}</Td>
              <Td textAlign="center">{bank.ifsc}</Td>
              <Td textAlign="center">{bank.acc_ob}</Td>
              <Td textAlign="center">{bank.acc_cb}</Td>

              <Td textAlign="center">
                <Flex justify="center" gap={4} align="center">
                  <CustomToolTip
                    button={
                      <FiEye
                        size={20}
                        color="#0175c2"
                        cursor="pointer"
                        onClick={() => viewBankDetails(bank.id)}
                      />
                    }
                    mess="View Bank"
                  />
                  <CustomToolTip
                    button={
                      <FiTrash
                        size={20}
                        cursor="pointer"
                        color="#F00"
                        onClick={() => {
                          setDeleteId(bank.id);
                          setDeleteDialog(true);
                        }}
                      />
                    }
                    mess="Delete Bank"
                  />
                </Flex>
              </Td>
            </Tr>
          ))}
          initialValue={initialValue}
          page={page}
          rows={rows}
          total_value={filteredBanks?.length || 0}
          pagination
          onChange={handleRowsChange}
          onPrevious={handlePrevious}
          onNext={handleNext}
        />

        <ConfirmDialog
          isOpen={deleteDialog}
          onClose={() => setDeleteDialog(false)}
          onConfirm={handleDelete}
          type="delete"
          title="Delete Bank"
          message="Are you sure you want to delete this bank? This action cannot be undone and will remove all associated data."
          confirmText="Delete"
          cancelText="Cancel"
          isLoading={isLoading}
        />
  </>
)}
        
      </div>
    </>
  );
};


export default BankList;
